import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

// import { ExperienceEvents, HostEvents } from 'arise-bridge';
import { useAppDispatch, useAppSelector } from '@/state/store';

import {
    selectInventoryItem,
    setIsNewsTickerVisible,
    setCurrentlyViewingMedia,
    setCurrentlyViewingPrize,
    setIsInventoryOpen,
    setIsLeaderboardOpen,
    setStartShare,
    setIsLocalePickerPanelVisible,
} from '@/state/features/app';
import { awardBadge, awardTitle, setMissionComplete } from '@/state/features/profile';
import { queueNewsTickerItem, setNewsTickerTag } from '@/state/features/feed';
import { useAriseBridge } from '@/arise/bridge/useAriseBridge';
import { AriseBridgeHost } from '@/arise/bridge/AriseBridgeHost';
import { failedGame, submitFeedUpdate, queueAchievement } from '@/state/features/app';
import LoadingScreen from '../LoadingScreen';
import { sendEventV2 } from '@/utils/analytics';
import { userEvent, submitScore, getUniverseMetadata, getNewsTickerData, getAuthToken } from '@/arise/api';
import useTitanEvents from '@/components/IframeContainer/useTitanEvents';
import useHyperionEvents from '@/components/IframeContainer/useHyperionEvents';
import { homePath } from '@/theme/vars';
import { useNavigateToSignup } from '@/hooks/auth';
import DiscordAuthFlow, { DiscordAuthFlowMethods, QuestUnenrolledNotice } from '@/components/DiscordAuthFlow';
import { useMimasEvents } from '@/components/IframeContainer/useMimasEvents';
import { HOST_PLATFORM, IS_DISCORD_ACTIVITY } from '@/globals';
import { NewsTickerItemData, NewsTickerItemDataIdOnly } from '@/models/feed';
import { discordPlatform, isDiscordOnMobile, triggerDiscordShare } from '@/components/DiscordAuthFlow/useDiscord';
import { ViewableMedia } from '@/models/misc';
import usePushUserData from '@/components/IframeContainer/usePushUserData';
import { useLocation } from '@/hooks/location';
import { useTheme } from '@/theme';
import { getUITranslatedText } from '@/components/TranslatedText';

interface IframeContainerProps {
    src?: string;
    bridgeRef?: React.MutableRefObject<AriseBridgeHost>;
    arisewareProjectID?: string;
    onReady?: () => void;
    loadingBackgroundImageUrl?: string;
    loadingLogoImageUrl?: string;
    loadingText?: [
        { title: string; subtitle: string },
        { title: string; subtitle: string },
        { title: string; subtitle: string },
    ];
    forceInFrontOfUi?: boolean;
    style?: React.CSSProperties;
}

export default function IframeContainer({
    src,
    bridgeRef,
    arisewareProjectID,
    onReady,
    loadingBackgroundImageUrl,
    loadingLogoImageUrl,
    loadingText,
    forceInFrontOfUi,
    style,
}: IframeContainerProps) {
    const navigate = useNavigate();
    const { locationId } = useParams();
    const [iframeReady, setIframeReady] = useState(false);

    const iframeRef = useRef<HTMLIFrameElement>(null);
    const discordRef = useRef<DiscordAuthFlowMethods>();
    const locationRef = useRef<any>(null);
    const loggedIn = useAppSelector((state) => state.auth.loggedIn);
    const theme = useTheme();

    let iframeUrl = useMemo(() => {
        const url = new URL(window.location.href);
        const iframeUrlParam = url.searchParams.get('iframeUrl');
        if (iframeUrlParam) {
            src = iframeUrlParam;
        }

        const params = new URLSearchParams(window.location.search);
        params.delete('iframeUrl');

        const paramsString = params.toString();
        if (paramsString) {
            src = `${src}?${paramsString}`;
        }
        return src;
    }, [src]);

    // If iframeUrl is passed as a query param, use that instead

    const dispatch = useAppDispatch();

    const bridge = useAriseBridge(iframeRef, {
        arisewareProjectID,
    });
    // Expose bridge to parent
    if (bridgeRef) {
        bridgeRef.current = bridge;
    }

    const titanEvents = useTitanEvents(arisewareProjectID);
    const hyperionEvents = useHyperionEvents();
    const navigateToSignup = useNavigateToSignup();
    const mimasEvents = useMimasEvents(discordRef, bridge);

    const status = useAppSelector((state) => state.discord.questStatus);
    const checked = useAppSelector((state) => state.discord.questStatusChecked);
    locationRef.current = useLocation(locationId || '');

    useEffect(() => {
        console.log('[Arise FE] Quest status changed:', status, checked);
        bridge?.sendEvent('quest-status-update', { status, checked });
    }, [bridge, status, checked]);

    const { pushUserData, pushLocale, pushTranslations } = usePushUserData(bridge);

    const shouldArisewareBeMuted = useAppSelector((state) => state.app.shouldArisewareBeMuted);
    useEffect(() => {
        if (typeof shouldArisewareBeMuted === 'boolean') {
            bridge?.sendEvent('mute', shouldArisewareBeMuted);
        }
    }, [shouldArisewareBeMuted]);

    function sendAuthToken() {
        const token = getAuthToken();
        bridge.sendEvent('arise-token', token);
    }

    function sendCCSettings() {
        // Using ref here because this function is used in a useEffect with no location dependency
        const settings = locationRef.current?.arisewareSettings || undefined;
        bridge.sendEvent('custom-content-settings', settings);
    }

    /*
     * Initialisation Events
     */
    // Automatically send events when user is logged in
    useEffect(() => {
        if (loggedIn && bridge) {
            sendAuthToken();
        }
    }, [loggedIn, bridge]);

    // Send location custom content settings when they are loaded
    useEffect(() => {
        if (locationRef.current && bridge) {
            sendCCSettings();
        }
    }, [location, bridge]);

    useEffect(() => {
        if (!bridge) return;

        function onReadyHandler(data: any) {
            setIframeReady(data.detail);
            if (data.detail === true) {
                onReady();
            }
        }

        function onFailed() {
            dispatch(failedGame({ arisewareProjectID }));
        }

        function onFeedUpdate(data: any) {
            dispatch(submitFeedUpdate(data.detail));
        }

        function onQueueAchievement(data: any) {
            dispatch(queueAchievement(data.detail));
        }

        function onSetNewsTickerVisibility(data: { detail: boolean }) {
            dispatch(setIsNewsTickerVisible(data.detail));
        }

        function onSetLocalePickerVisibility(data: { detail: boolean }) {
            dispatch(setIsLocalePickerPanelVisible(data.detail));
        }

        function onSetInventoryIsOpen(data: any) {
            dispatch(setIsInventoryOpen(data.detail));
        }

        function onShowLeaderboard(data: { detail: boolean }) {
            dispatch(setIsLeaderboardOpen(data.detail));
        }

        async function onSubmitScore(data: { detail: number }) {
            try {
                await submitScore(data.detail);
            } catch (error) {
                console.log('Failed to submit score', error);
            }
        }

        function onSelectInventoryItem() {
            dispatch(selectInventoryItem());
        }

        function onTrack(data: {
            detail: {
                eventType: string;
                metadata: {
                    strings: string[];
                    numbers: number[];
                };
            };
        }) {
            const { eventType, metadata } = data.detail;
            if (typeof eventType !== 'string') {
                console.error('Cannot send event from iFrame - eventType is invalid or missing:', data.detail);
                return;
            }
            sendEventV2(eventType, metadata?.strings, metadata?.numbers, true);
        }

        function onCheckpoint(data: {
            detail: {
                name: string;
                time: number;
                id: number;
            };
        }) {
            const { name, time, id } = data.detail;
            sendEventV2('checkpoint', [name], [time, id]);
        }

        function onGotoHome() {
            navigate(homePath);
        }

        function onGotoSignup() {
            navigateToSignup();
        }

        function onViewMedia(data: { detail: ViewableMedia }) {
            dispatch(setCurrentlyViewingMedia(data.detail));
        }

        function onViewPrize(data: any) {
            dispatch(setCurrentlyViewingPrize(data.detail));
        }

        function onCopyText(data: any) {
            try {
                navigator.clipboard.writeText(data.detail);
            } catch (error) {
                console.error('Bridge event copy-text could not write to clipboard: ', error);
            }
        }

        function onOpenUrl(data: { detail: string }) {
            sendEventV2('external_link_opened', [data.detail]);
            if (IS_DISCORD_ACTIVITY) {
                discordRef.current.openExternalLink(data.detail);
            } else {
                window.open(data.detail, '_blank');
            }
        }

        function onQuestCompleted() {
            if (IS_DISCORD_ACTIVITY && discordRef.current) {
                discordRef.current.completeQuest();
            }
            dispatch(setMissionComplete());
        }

        function onQuestProgress(data: { detail: number }) {
            if (IS_DISCORD_ACTIVITY && discordRef.current) {
                discordRef.current.setQuestProgress(data.detail);
            }
        }

        function onBotRegister() {
            if (IS_DISCORD_ACTIVITY && discordRef.current) {
                discordRef.current.botRegister();
            }
        }

        async function onSendUserEvent(data: { detail: NewsTickerItemData }) {
            const isNewsTickerDataIdOnlyItem = (item: NewsTickerItemData): item is NewsTickerItemDataIdOnly => {
                return 'id' in item;
            };

            const item = data?.detail;
            if (item) {
                dispatch(queueNewsTickerItem(item));
                if (isNewsTickerDataIdOnlyItem(item)) {
                    // If is ID only item, send to backend too
                    try {
                        await userEvent(locationId, item.id);
                    } catch (error) {
                        console.error('Bridge event sendUserEvent could not send event:', error);
                    }
                }
            }
        }

        function onSetNewsTickerTag(data: { detail: string }) {
            dispatch(setNewsTickerTag(data.detail));
        }

        function onShare(data: { detail?: { message?: string; disableAutoLink?: boolean; linkId?: string } }) {
            // If no custom message passed in, use the share text from the CMS
            const fallbackCmsShareText = theme.metadata?.customJSON?.share?.text;
            const translation_shareText = getUITranslatedText('informationScreen_shareText', fallbackCmsShareText);
            const { message = translation_shareText, disableAutoLink, linkId } = data.detail ?? {};
            if (IS_DISCORD_ACTIVITY) {
                triggerDiscordShare(message, disableAutoLink, linkId);
            } else {
                dispatch(setStartShare({ message, disableAutoLink }));
            }
        }

        function onAwardBadge(data: { detail: string }) {
            const badgeRank = data.detail;
            dispatch(awardBadge(badgeRank));
        }

        function onAwardTitle(data: { detail: string }) {
            const titleId = data.detail;
            dispatch(awardTitle(titleId));
        }

        async function onGetQuestStatus() {
            if (IS_DISCORD_ACTIVITY && discordRef.current) {
                const status = await discordRef.current.getQuestStatus();
                bridge.sendEvent('quest-status-update', status);
            }
        }

        async function onGetUniverseMetadata() {
            try {
                const json = await getUniverseMetadata();
                bridge.sendEvent('universe-metadata', json);
            } catch (error) {
                console.log('[Arise] Error cannot getUniverseMetadata');
            }
        }

        function onGetTranslations() {
            pushTranslations();
        }

        function onGetLocale() {
            pushLocale();
        }

        async function onGetNewsTickerData() {
            try {
                const json = await getNewsTickerData();
                bridge.sendEvent('news-ticker-data', json);
            } catch (error) {
                console.log('[Arise] Error cannot getNewsTickerData');
            }
        }

        function pushDiscordPlatform() {
            if (IS_DISCORD_ACTIVITY) {
                const platform = discordPlatform();
                bridge.sendEvent('discord-platform', platform);
            }
        }

        function onGetAuthToken() {
            sendAuthToken();
        }

        function getGetCCSettings() {
            sendCCSettings();
        }

        function onGetHostPlatform() {
            bridge.sendEvent('host-platform', HOST_PLATFORM);
        }

        bridge.on('ready', onReadyHandler);
        bridge.on('ready', pushUserData);
        bridge.on('ready', pushDiscordPlatform);
        bridge.on('failed', onFailed);
        bridge.on('feed-update', onFeedUpdate);
        bridge.on('queue-achievement', onQueueAchievement);
        bridge.on('set-news-ticker-visibility', onSetNewsTickerVisibility);
        bridge.on('set-locale-picker-visibility', onSetLocalePickerVisibility);
        bridge.on('set-inventory-is-open', onSetInventoryIsOpen);
        bridge.on('show-leaderboard', onShowLeaderboard);
        bridge.on('submit-score', onSubmitScore);
        bridge.on('select-inventory-item', onSelectInventoryItem);
        bridge.on('track', onTrack);
        bridge.on('track:checkpoint', onCheckpoint);
        bridge.on('goto_home', onGotoHome);
        bridge.on('goto_signup', onGotoSignup);
        bridge.on('view-media', onViewMedia);
        bridge.on('view-prize', onViewPrize);
        bridge.on('copy-text', onCopyText);
        bridge.on('send-user-event', onSendUserEvent);
        bridge.on('news-ticker-tag', onSetNewsTickerTag);
        bridge.on('share', onShare);
        bridge.on('open-url', onOpenUrl);
        bridge.on('quest-completed', onQuestCompleted);
        bridge.on('quest-progress', onQuestProgress);
        bridge.on('get-quest-status', onGetQuestStatus);
        bridge.on('bot-register', onBotRegister);
        bridge.on('get-universe-metadata', onGetUniverseMetadata);
        bridge.on('get-news-ticker-data', onGetNewsTickerData);
        bridge.on('get-translations', onGetTranslations);
        bridge.on('get-locale', onGetLocale);
        bridge.on('award-badge', onAwardBadge);
        bridge.on('award-title', onAwardTitle);
        bridge.on('get-arise-token', onGetAuthToken);
        bridge.on('get-custom-content-settings', getGetCCSettings);
        bridge.on('get-host-platform', onGetHostPlatform);

        if (process.env.PUBLIC_INSTANCE === 'titan') {
            bridge.on('goto_camp', titanEvents.onGotoCamp);
            bridge.on('goto', titanEvents.onGoto);
            bridge.on('submitresult', titanEvents.onSubmitResult);
        }

        if (process.env.PUBLIC_INSTANCE === 'hyperion') {
            bridge.on('goto', hyperionEvents.onGoto);
        }

        if (process.env.PUBLIC_INSTANCE === 'mimas') {
            bridge.on('ready', mimasEvents.pushCountryCode);
            bridge.on('ariseware-checkpoint-reached', mimasEvents.onCheckpointReached);
        }

        return () => {
            dispatch(setIsNewsTickerVisible(true));
            bridge.off('ready', onReady);
            bridge.off('ready', pushUserData);
            bridge.off('failed', onFailed);
            bridge.off('feed-update', onFeedUpdate);
            bridge.off('queue-achievement', onQueueAchievement);
            bridge.off('set-news-ticker-visibility', onSetNewsTickerVisibility);
            bridge.off('set-inventory-is-open', onSetInventoryIsOpen);
            bridge.off('show-leaderboard', onShowLeaderboard);
            bridge.off('submit-score', onSubmitScore);
            bridge.off('select-inventory-item', onSelectInventoryItem);
            bridge.off('track', onTrack);
            bridge.off('track:checkpoint', onCheckpoint);
            bridge.off('goto_home', onGotoHome);
            bridge.off('goto_signup', onGotoSignup);
            bridge.off('view-media', onViewMedia);
            bridge.off('view-prize', onViewPrize);
            bridge.off('copy-text', onCopyText);
            bridge.off('send-user-event', onSendUserEvent);
            bridge.off('news-ticker-tag', onSetNewsTickerTag);
            bridge.off('share', onShare);
            bridge.off('open-url', onOpenUrl);
            bridge.off('quest-completed', onQuestCompleted);
            bridge.off('quest-progress', onQuestProgress);
            bridge.off('get-quest-status', onGetQuestStatus);
            bridge.off('bot-register', onBotRegister);
            bridge.off('get-universe-metadata', onGetUniverseMetadata);
            bridge.off('get-news-ticker-data', onGetNewsTickerData);
            bridge.off('get-translations', onGetTranslations);
            bridge.off('get-locale', onGetLocale);
            bridge.off('award-badge', onAwardBadge);
            bridge.off('award-title', onAwardTitle);
            bridge.off('get-arise-token', onGetAuthToken);
            bridge.off('get-custom-content-settings', getGetCCSettings);
            bridge.off('get-host-platform', onGetHostPlatform);
            bridge.off('set-locale-picker-visibility', onSetLocalePickerVisibility);

            if (process.env.PUBLIC_INSTANCE === 'titan') {
                bridge.off('goto_camp', titanEvents.onGotoCamp);
                bridge.off('goto', titanEvents.onGoto);
                bridge.off('submitresult', titanEvents.onSubmitResult);
            }
            if (process.env.PUBLIC_INSTANCE === 'hyperion') {
                bridge.off('goto', hyperionEvents.onGoto);
            }
            if (process.env.PUBLIC_INSTANCE === 'mimas') {
                bridge.off('ariseware-checkpoint-reached', mimasEvents.onCheckpointReached);
                bridge.off('ready', mimasEvents.pushCountryCode);
            }
        };
    }, [bridge, iframeUrl]);

    const forceInFrontOfUIStyle: React.CSSProperties = forceInFrontOfUi ? { position: 'absolute', zIndex: 100000 } : {};

    return (
        <>
            <iframe
                allow="xr-spatial-tracking; autoplay; encrypted-media;"
                ref={iframeRef}
                src={iframeUrl}
                style={{
                    width: '100%',
                    height: '100%',
                    opacity: iframeReady ? 1 : 0,
                    ...style,
                    ...forceInFrontOfUIStyle,
                }}
            />
            <LoadingScreen
                show={!iframeReady}
                backgroundImageUrl={loadingBackgroundImageUrl}
                logoImageUrl={loadingLogoImageUrl}
                loadingText={loadingText}
                style={forceInFrontOfUIStyle}
            />
            {IS_DISCORD_ACTIVITY && <DiscordAuthFlow ref={discordRef} />}
        </>
    );
}
